import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { ADMIN_DAILY_TRANSACTION_REPORT } from './../../../Utils/appConstants';
import { getRequest } from './../../../Utils/AppApi';
import { INRFormat } from './../../../Utils/Common';
import { useHistory } from "react-router-dom";

import { DatePicker, Tooltip } from 'antd';
import { getUTCToLocalDateTimeFormat, toDayDate, queryDate, getExportFormat } from './../../../Utils/function';
import PropagateLoader from "react-spinners/PropagateLoader";
import {
    FilterOutlined,
    SearchOutlined,
    ClearOutlined,
    DownloadOutlined,
} from '@ant-design/icons';

/* ============================================================
   MODERN STYLES (scoped) — identical to AePS / FundTransfer
============================================================ */
const reportStyles = `
.aeps-page {
    padding: 16px 24px 40px;
    background: #f5f7fb;
    min-height: calc(100vh - 60px);
}
.aeps-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    flex-wrap: wrap;
    gap: 12px;
}
.aeps-page-title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2a44;
    margin: 0;
    letter-spacing: 0.2px;
}
.aeps-page-title span { color: #e5484d; }
.aeps-page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 2px 0 0;
}

.aeps-card {
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06);
    border: 1px solid #eef0f4;
    overflow: hidden;
}
.aeps-card + .aeps-card { margin-top: 18px; }

.aeps-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f2f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}
.aeps-card-head-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
    text-transform: uppercase;
    letter-spacing: .6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}
.aeps-card-head-title .anticon {
    color: #5851D2;
    font-size: 14px;
}

/* Filter grid: 6 cols; row1 = 4 fields; row2 = full-width right-aligned actions */
.aeps-filter-body { padding: 18px 20px 20px; }

.aeps-filter-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px 16px;
    align-items: end;
}

.aeps-field { display: flex; flex-direction: column; min-width: 0; }
.aeps-field label {
    font-size: 11.5px;
    font-weight: 600;
    color: #667085;
    text-transform: uppercase;
    letter-spacing: .4px;
    margin-bottom: 6px;
}

.aeps-field .ant-picker,
.aeps-field .aeps-input {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e4e7ec;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
    font-size: 13.5px;
}
.aeps-field .ant-picker { padding: 0 11px; }
.aeps-field .ant-picker input { font-size: 13.5px; }
.aeps-field .ant-picker:hover,
.aeps-field .aeps-input:hover { border-color: #c7cbd3 !important; }
.aeps-field .ant-picker-focused,
.aeps-field .aeps-input:focus {
    border-color: #5851D2 !important;
    box-shadow: 0 0 0 3px rgba(88,81,210,.12) !important;
    outline: none;
}

.aeps-input {
    padding: 0 12px;
    color: #101828;
    outline: none;
}
.aeps-input::placeholder { color: #98a2b3; }

/* Actions row — full width, right-aligned */
.aeps-filter-grid .aeps-actions {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 10px;
    min-height: 40px;
    padding-top: 4px;
}

/* ── Buttons ─────────────────────────────────────────── */
.aeps-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform .08s, box-shadow .15s, background .15s, border-color .15s;
    white-space: nowrap;
}
.aeps-btn:active { transform: translateY(1px); }

.aeps-btn-primary {
    background: linear-gradient(180deg, #6b64e0 0%, #5851D2 100%);
    color: #fff;
    box-shadow: 0 2px 6px rgba(88,81,210,.35);
}
.aeps-btn-primary:hover { box-shadow: 0 4px 12px rgba(88,81,210,.45); }

.aeps-btn-ghost {
    background: #ffffff;
    color: #475467;
    border-color: #e4e7ec;
}
.aeps-btn-ghost:hover { background: #f9fafb; border-color: #cfd4dc; }

.aeps-btn-success {
    background: #ffffff;
    color: #0e9384;
    border-color: #b7e4dd;
}
.aeps-btn-success:hover { background: #f0fdfa; border-color: #0e9384; }

/* ── Table ───────────────────────────────────────────── */
.aeps-table-wrap { padding: 4px 6px 10px; }
.aeps-table-wrap .rdt_TableHeadRow {
    background: #f9fafb !important;
    border-bottom: 1px solid #eef0f4 !important;
    min-height: 46px !important;
}
.aeps-table-wrap .rdt_TableCol {
    font-size: 11.5px !important;
    font-weight: 700 !important;
    color: #475467 !important;
    text-transform: uppercase !important;
    letter-spacing: .4px !important;
}
.aeps-table-wrap .rdt_TableRow {
    border-bottom: 1px solid #f2f4f7 !important;
    min-height: 52px !important;
    transition: background .15s;
}
.aeps-table-wrap .rdt_TableRow:hover { background: #fafbff !important; }
.aeps-table-wrap .rdt_TableCell { font-size: 13px !important; color: #344054 !important; }
.aeps-table-wrap .rdt_Pagination {
    border-top: 1px solid #eef0f4 !important;
    background: #fff !important;
    min-height: 52px !important;
}

/* ── Responsive ── */
@media (max-width: 1023px) {
    .aeps-filter-grid {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    .aeps-filter-grid .aeps-actions {
        grid-column: 1 / -1;
        margin-top: 6px;
        padding-top: 16px;
        border-top: 1px dashed #eef0f4;
    }
}

@media (max-width: 640px) {
    .aeps-page { padding: 12px; }
    .aeps-filter-grid .aeps-actions { justify-content: center; }
    .aeps-filter-grid .aeps-actions .aeps-btn {
        flex: 1 1 auto;
        justify-content: center;
    }
}
`;

/* ============================================================
   HELPERS
============================================================ */
const normalizeServiceName = (name) => {
    if (!name) return name;
    return /^DMT/i.test(String(name)) ? 'DMT' : name;
};

const getTransactionAmount = (row) => {
    const amt = Number(row.transactionAmt) || 0;
    if (amt > 0) return amt;
    const dr = Number(row.debit) || 0;
    if (dr > 0) return dr;
    const cr = Number(row.credit) || 0;
    if (cr > 0) return cr;
    return 0;
};

const getTransactionType = (row) => {
    if (Number(row.debit) > 0) return 'DR';
    if (Number(row.credit) > 0) return 'CR';
    return '-';
};

/* ============================================================ */

function DailyTransactionReport() {

    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    const [isLoading, setLoading] = useState(false);
    const [searchUserId, setSearchUserId] = useState('');
    const [searchServiceName, setSearchServiceName] = useState('');
    const [searchTransactionId, setSearchTransactionId] = useState('');
    const [startDate, setStartDate] = useState(toDayDate());
    const [getData, setData] = useState({ docs: [] });
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(limit);
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [tableResetKey, setTableResetKey] = useState(0);

    // ============================================================
    //  COLUMNS
    // ============================================================
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: '200px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Trans ID',
            selector: row => (
                <Tooltip placement="bottom" title={row.transactionId}>
                    {row.transactionId}
                </Tooltip>
            ),
            width: '180px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Ref ID / Name',
            selector: row => row.users ? `${row.users.username || '-'} / ${row.users.name || '-'}` : '-',
            width: '250px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Pancard',
            selector: row => (row.users && row.users.pancard) ? row.users.pancard : '-',
            width: '150px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'State',
            selector: row => (row.shopsState && row.shopsState.length > 0) ? row.shopsState[0] : (row.stateName || '-'),
            width: '150px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Service Name',
            selector: row => (
                <Tooltip placement="bottom" title={normalizeServiceName(row.serviceName)}>
                    {normalizeServiceName(row.serviceName)}
                </Tooltip>
            ),
            width: '200px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Tran Amt',
            selector: row => {
                const amt = getTransactionAmount(row);
                return amt > 0 ? INRFormat(amt) : '-';
            },
            width: '150px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Tran type',
            selector: row => getTransactionType(row),
            width: '120px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Opening Balance',
            selector: row => row.openingBalance ? INRFormat(row.openingBalance) : '-',
            width: '150px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Debit',
            selector: row => (!row.debit) ? '-' : INRFormat(row.debit),
            width: '130px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Credit',
            selector: row => (!row.credit) ? '-' : INRFormat(row.credit),
            width: '130px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Ser. Charge',
            selector: row => row.serviceCharge ? INRFormat(row.serviceCharge) : '-',
            width: '130px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Commission',
            selector: row => row.commission ? INRFormat(row.commission) : '-',
            width: '130px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'TDS',
            selector: row => row.tds ? INRFormat(row.tds) : '-',
            width: '100px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'TDS Applicable',
            selector: row => row.tds ? 'YES' : 'NO',
            width: '130px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Closing Balance',
            selector: row => INRFormat(row.closingBalance),
            width: '150px',
            headerStyle: () => ({ textAlign: 'center' }),
        },
        {
            name: 'Description',
            selector: row => row.description
                ? <Tooltip placement="bottom" title={row.description}>{row.description}</Tooltip>
                : '-',
            width: '250px',
        }
    ];

    // ============================================================
    //  HELPERS
    // ============================================================
    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, p)) {
                str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
            }
        }
        return str.join('&');
    };

    // ============================================================
    //  FETCH
    // ============================================================
    const getDataReports = () => {
        setLoading(true);

        const params = { page, limit: isLimit };

        if (startDate) {
            params.fromDate = queryDate(startDate);
            params.toDate = queryDate(startDate);
        }
        params.searchUserId = searchUserId || '';
        params.searchServiceName = searchServiceName || '';
        params.searchTransactionId = searchTransactionId || '';

        const queryString = objectToQueryString(params);
        getRequest(`${ADMIN_DAILY_TRANSACTION_REPORT}?${queryString}`, history)
            .then((response) => {
                if (response.success === true && response.data) {
                    setData(response.data);
                    setTotalRecord(response.data.totalDocs || 0);
                    if (typeof response.data.page === 'number') setPage(response.data.page);
                } else {
                    setData({ docs: [] });
                    setTotalRecord(0);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setData({ docs: [] });
                setTotalRecord(0);
                setLoading(false);
            });
    };

    useEffect(() => {
        getDataReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTrigger]);

    // ============================================================
    //  CSV EXPORT
    // ============================================================
    const getCSVDataList = () => {
        const params = { exportsCsv: true };
        if (startDate) {
            params.fromDate = queryDate(startDate);
            params.toDate = queryDate(startDate);
        }
        params.searchUserId = searchUserId || '';
        params.searchServiceName = searchServiceName || '';
        params.searchTransactionId = searchTransactionId || '';

        const queryString = objectToQueryString(params);
        getRequest(`${ADMIN_DAILY_TRANSACTION_REPORT}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    const csvdataRecord = (response.data || []).map((e) => ({
                        'Created Date': getExportFormat(e.createdAt),
                        'Updated Date': getExportFormat(e.updatedAt),
                        'TXT Id': e.transactionId,
                        'Ref ID': e.users?.username || '',
                        'Pancard': e.users?.pancard || '',
                        'State': (e.shopsState && e.shopsState.length > 0) ? e.shopsState[0] : e.stateName,
                        'Name': e.users?.name || '',
                        'Transaction Amt': getTransactionAmount(e),
                        'Transaction Type': getTransactionType(e),
                        'Service Name': normalizeServiceName(e.serviceName),
                        'Debit': e.debit,
                        'Credit': e.credit,
                        'Service Charge': e.serviceCharge,
                        'Commission': e.commission,
                        'TDS': e.tds,
                        'TDS Applicable': e.tds ? 'YES' : 'NO',
                        'Opening Balance': e.openingBalance,
                        'Closing Balance': e.closingBalance,
                        'Remark': e.description
                    }));
                    setCsvData(csvdataRecord);
                    setTimeout(() => {
                        if (csvLink.current && csvLink.current.link) {
                            csvLink.current.link.click();
                        }
                    }, 1000);
                } else {
                    setCsvData([]);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    // ============================================================
    //  HANDLERS
    // ============================================================
    const resetToFirstPageAndFetch = () => {
        setPage(1);
        setTableResetKey((k) => k + 1);
        setFetchTrigger((k) => k + 1);
    };

    const handlePageChange = (newPage /*, size */) => {
        setPage(newPage);
        setFetchTrigger((k) => k + 1);
    };

    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        resetToFirstPageAndFetch();
    };

    const handleSearch = () => resetToFirstPageAndFetch();

    const handleClear = () => {
        setStartDate(toDayDate());
        setSearchUserId('');
        setSearchServiceName('');
        setSearchTransactionId('');
        resetToFirstPageAndFetch();
    };

    // ============================================================
    //  RENDER
    // ============================================================
    return (
        <div className="aeps-page">
            <style>{reportStyles}</style>

            {/* ─── Page header ─────────────────────────────────── */}
            <div className="aeps-page-header">
                <div>
                    <h1 className="aeps-page-title">
                        Daily Transaction <span>Report</span>
                    </h1>
                    <p className="aeps-page-subtitle">
                        Search, filter, and export daily transaction ledger
                    </p>
                </div>
            </div>

            {/* ─── Filters card ───────────────────────────────── */}
            <div className="aeps-card">
                <div className="aeps-card-head">
                    <h2 className="aeps-card-head-title">
                        <FilterOutlined /> Filters
                    </h2>
                </div>

                <div className="aeps-filter-body">
                    <div className="aeps-filter-grid">
                        <div className="aeps-field">
                            <label>From Date</label>
                            <DatePicker
                                value={startDate}
                                format={dateFormat}
                                onChange={(date) => setStartDate(date)}
                                placeholder="Select date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>User ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter User ID"
                                onChange={(e) => setSearchUserId(e.target.value)}
                                value={searchUserId}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Service Name</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Service Name"
                                onChange={(e) => setSearchServiceName(e.target.value)}
                                value={searchServiceName}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Transaction ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Transaction ID"
                                onChange={(e) => setSearchTransactionId(e.target.value)}
                                value={searchTransactionId}
                            />
                        </div>

                        {/* Actions — full-width row, right-aligned */}
                        <div className="aeps-actions">
                            <button type="button" className="aeps-btn aeps-btn-ghost" onClick={handleClear}>
                                <ClearOutlined /> Clear
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-success" onClick={getCSVDataList}>
                                <DownloadOutlined /> Export CSV
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-primary" onClick={handleSearch}>
                                <SearchOutlined /> Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Table card ─────────────────────────────────── */}
            <div className="aeps-card">
                <div className="aeps-table-wrap">
                    <DataTable
                        key={tableResetKey}
                        title=""
                        columns={columns}
                        data={getData.docs || []}
                        highlightOnHover
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        paginationDefaultPage={page}
                        paginationPerPage={isLimit}
                        progressPending={isLoading}
                        progressComponent={
                            <div className="text-center py-4">
                                <PropagateLoader color="#5851D2" size={20} />
                            </div>
                        }
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        noDataComponent={
                            <div style={{ padding: '40px 20px', color: '#98a2b3', fontSize: 13 }}>
                                No records found for the selected filters.
                            </div>
                        }
                    />
                </div>
            </div>

            {/* hidden CSV trigger */}
            <CSVLink
                data={getCsvData}
                filename={`${startDate ? queryDate(startDate) : ''}-daily-transaction`}
                className="hidden"
                ref={csvLink}
                target="_blank"
                style={{ display: 'none' }}
            />
        </div>
    );
}

export default DailyTransactionReport;